import { of } from 'rxjs';
import { concatAll, concatMap, filter, map, mergeAll, mergeMap } from 'rxjs/operators';

import { Store } from '@ngrx/store';
import { Actions } from '@ngrx/effects';

import { mergeHotStreamHandlerArray } from '../../rxjs/index';

import { EFFECT_TYPE } from '../effect-type';
import { EffectConfiguration, EffectsConfiguration } from '../effects-configuration';
import { EffectsRepository } from '../effects-repository';

/******************************************************
 *
 * EffectsConfigurator
 *
 ******************************************************/

export class EffectsConfigurator {

    private _setupMethodMap: any = {
        [EFFECT_TYPE.SEQUENCE]: this._setUpSequentialEffect,
        [EFFECT_TYPE.PARALLEL]: this._setUpParallelEffect,
        [EFFECT_TYPE.ACTION  ]: this._setUpActionEffect
    };

    private _effectsRepository: EffectsRepository;

    private _effect: any;
    private _effects: Function[] = [];

    constructor(
        effectsRepository: EffectsRepository,
        actions$: Actions,
        store: Store<any>
   ) {

        this._effectsRepository = effectsRepository;

        this._effect = actions$.pipe(
            mergeHotStreamHandlerArray(this._effects),
            filter(action => this._isAction(action) )
        );

        this._effect.subscribe( (action: any) => {
            store.dispatch(action);
        });
    }

    public addEffects(effectsConfig: EffectsConfiguration = []): any {

        effectsConfig.forEach( config => {
            const method = this._setupMethodMap[config.type].call(this, config);
            this._effects.push( method );
        });
    }

    private _isAction(action: any): boolean {
        return action && action.type && (typeof action.type === 'string');
    }

    private _setUpSequentialEffect(config: EffectConfiguration): Function {

        return (action: any) => of(action).pipe(
            filter( action => this._filterAction(action, config) ),
            concatMap (
                action => config.handlers.map(
                    handler => {
                        const handlerFn = this._getHandler(handler, config);
                        return handlerFn(action, config);
                    }
                )
            ),
            concatAll()
        );
    }

    private _setUpParallelEffect(config: EffectConfiguration): Function {

        return action => of(action).pipe(
            filter( action => this._filterAction(action, config) ),
            mergeMap (
                action => config.handlers.map(
                    handler => {
                        const handlerFn = this._getHandler(handler, config);
                        return handlerFn(action, config);
                    }
                )
            ),
            mergeAll()
        );
    }

    private _setUpActionEffect(config: EffectConfiguration): Function {

        return action => of(action).pipe(
            filter( action => this._filterAction(action, config) ),
            map( action => {
                return {
                    type: config.result,
                    payload: action['payload']
                };
            })
        );
    }

    private _filterAction(action: any, effectsConfig: EffectConfiguration): boolean  {
        const isCause = effectsConfig.causes.indexOf(action.type) > -1;
        const isWildcard = effectsConfig.causes.indexOf('*') > -1;

        return isWildcard || isCause;
    }

    private _getHandler(handler: string | Function, config: any): Function {

        return ( typeof handler === 'function' )
            ? handler
            : this._effectsRepository.get(handler);
    }
}

